/**
 * 경매 출품 — 도막 입력 그림 (헤이딜러 스타일)
 *
 * CarPaintMap.js 의 SVG 11 부위 구조 + onClick 입력 modal.
 * Props:
 *   value: { [partKey]: μm INTEGER } — paintData
 *   onChange: (next) => void
 *
 * 부위 클릭 → PartInputModal 띄움 → 정상(120) / 의심(180) / 판금(300) preset 또는 μm 직접 입력.
 * 색상 즉시 반영 (CarPaintMap 동일 임계값).
 *
 * Z-index 가드 (advisor #3): modal-overlay position:fixed inset:0 z-index:9999 — SVG 부위 onClick 차단.
 */
import React, { useState } from 'react';

var PART_LABELS = {
  hood: '본넷',
  roof: '루프',
  trunk: '트렁크',
  fenderL: '좌앞펜더', fenderR: '우앞펜더',
  doorFL: '좌앞문',   doorFR: '우앞문',
  doorRL: '좌뒷문',   doorRR: '우뒷문',
  quarterL: '좌뒤펜더', quarterR: '우뒤펜더',
};

// CarPaintMap.js:7-23 동일 임계값
var getPaintColor = function(v) {
  if (!v) return '#D3D1C7';
  var n = typeof v === 'string' ? parseInt(v, 10) : v;
  if (isNaN(n)) return '#D3D1C7';
  if (n < 150) return '#1D9E75';
  if (n < 250) return '#EF9F27';
  return '#E24B4A';
};

var getPaintBg = function(v) {
  if (!v) return '#f5f5f2';
  var n = typeof v === 'string' ? parseInt(v, 10) : v;
  if (isNaN(n)) return '#f5f5f2';
  if (n < 150) return '#E1F5EE';
  if (n < 250) return '#FAEEDA';
  return '#FCEBEB';
};

// 3 단계 preset — Kyle 결정 (modal 분기)
var PRESETS = [
  { key: 'normal',  label: '정상',  value: 120, color: '#1D9E75', bg: '#E1F5EE', desc: '< 150μm' },
  { key: 'caution', label: '의심',  value: 180, color: '#EF9F27', bg: '#FAEEDA', desc: '150~250μm' },
  { key: 'repaint', label: '판금',  value: 300, color: '#E24B4A', bg: '#FCEBEB', desc: '≥ 250μm' },
];

function PartInputModal(props) {
  var partKey = props.partKey;
  var initial = props.currentValue;
  var [manual, setManual] = useState(initial != null ? String(initial) : '');

  var label = PART_LABELS[partKey] || partKey;

  var pickPreset = function(p) {
    props.onSelect(p.value);
  };

  var pickManual = function() {
    var n = parseInt(manual, 10);
    if (isNaN(n) || n < 0 || n > 999) return;
    props.onSelect(n);
  };

  return (
    <div onClick={props.onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={function(e){e.stopPropagation();}}
        style={{ background: '#fff', borderRadius: 14, padding: '18px 18px 14px', maxWidth: 360, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#1B3A5C' }}>🎨 {label}</div>
          <button onClick={props.onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', padding: 0 }}>✕</button>
        </div>

        {/* 3 단계 preset */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 12 }}>
          {PRESETS.map(function(p) {
            return (
              <button key={p.key} onClick={function(){pickPreset(p);}}
                style={{ padding: '10px 6px', background: p.bg, border: '2px solid '+p.color, borderRadius: 8, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: p.color }}>{p.label}</span>
                <span style={{ fontSize: 9, color: p.color, fontWeight: 600 }}>{p.desc}</span>
              </button>
            );
          })}
        </div>

        {/* μm 직접 입력 */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10, marginBottom: 8 }}>
          <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>또는 정확 μm 직접 입력</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="text" inputMode="numeric"
              value={manual}
              onChange={function(e){setManual(e.target.value.replace(/[^0-9]/g,'').slice(0,3));}}
              onKeyDown={function(e){if(e.key==='Enter')pickManual();}}
              placeholder="예: 145"
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 700, textAlign: 'center', outline: 'none' }} />
            <button onClick={pickManual} disabled={!manual}
              style={{ padding: '8px 14px', background: manual ? '#1B3A5C' : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: manual ? 'pointer' : 'not-allowed' }}>
              입력
            </button>
          </div>
        </div>

        {/* 해제 */}
        {initial != null && (
          <button onClick={props.onClear}
            style={{ width: '100%', padding: '8px 0', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11, color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}>
            해제 (입력값 제거)
          </button>
        )}
      </div>
    </div>
  );
}

function AuctionPaintMap(props) {
  var value = props.value || {};
  var onChange = props.onChange;
  var [selectedPart, setSelectedPart] = useState(null);

  var handleSelect = function(num) {
    var next = Object.assign({}, value);
    next[selectedPart] = num;
    if (onChange) onChange(next);
    setSelectedPart(null);
  };

  var handleClear = function() {
    var next = Object.assign({}, value);
    delete next[selectedPart];
    if (onChange) onChange(next);
    setSelectedPart(null);
  };

  // CarPaintMap.js Part 와 동일 SVG 구조 + onClick
  var Part = function(p) {
    var v = value[p.partKey];
    var color = getPaintColor(v);
    var bg = getPaintBg(v);
    return (
      <g onClick={function(){setSelectedPart(p.partKey);}} style={{ cursor: 'pointer' }}>
        <rect x={p.x} y={p.y} width={p.w} height={p.h} rx={p.rx || 0} fill={bg} stroke={color} strokeWidth="1.5" />
        <text x={p.x + p.w / 2} y={p.y + p.h / 2 - 6} textAnchor="middle" fontSize="9" fill="#888" fontWeight="500" style={{ pointerEvents: 'none' }}>{p.label}</text>
        <text x={p.x + p.w / 2} y={p.y + p.h / 2 + 9} textAnchor="middle" fontSize="14" fill={color} fontWeight="700" style={{ pointerEvents: 'none' }}>{v != null ? v : '+'}</text>
      </g>
    );
  };

  return (
    <div>
      <svg viewBox="0 0 260 410" width="100%" style={{ display: 'block', maxWidth: 280, margin: '0 auto' }}>
        <text x="130" y="14" textAnchor="middle" fontSize="10" fill="#bbb">▲ 전면</text>
        <text x="130" y="405" textAnchor="middle" fontSize="10" fill="#bbb">▼ 후면</text>

        <path d="M75 30 L185 30 Q210 30 215 55 L215 340 Q215 365 185 365 L75 365 Q50 365 45 340 L45 55 Q45 30 75 30 Z"
          fill="none" stroke="#ccc" strokeWidth="1.2" />
        <path d="M65 108 L195 108 L200 80 Q195 60 185 45 L75 45 Q65 60 58 80 Z"
          fill="none" stroke="#ddd" strokeWidth="0.7" strokeDasharray="4 3" />
        <path d="M65 282 L195 282 L200 310 Q195 335 185 350 L75 350 Q65 335 58 310 Z"
          fill="none" stroke="#ddd" strokeWidth="0.7" strokeDasharray="4 3" />

        <Part partKey="hood"     label="본넷"   x={65}  y={38}  w={130} h={68} rx={8} />
        <Part partKey="roof"     label="루프"   x={65}  y={112} w={130} h={166} rx={0} />
        <Part partKey="trunk"    label="트렁크" x={65}  y={284} w={130} h={68} rx={8} />
        <Part partKey="fenderL"  label="좌펜더" x={28}  y={38}  w={34}  h={68} rx={4} />
        <Part partKey="fenderR"  label="우펜더" x={198} y={38}  w={34}  h={68} rx={4} />
        <Part partKey="doorFL"   label="좌앞문" x={28}  y={112} w={34}  h={80} rx={0} />
        <Part partKey="doorFR"   label="우앞문" x={198} y={112} w={34}  h={80} rx={0} />
        <Part partKey="doorRL"   label="좌뒷문" x={28}  y={198} w={34}  h={80} rx={0} />
        <Part partKey="doorRR"   label="우뒷문" x={198} y={198} w={34}  h={80} rx={0} />
        <Part partKey="quarterL" label="좌뒤펜" x={28}  y={284} w={34}  h={68} rx={4} />
        <Part partKey="quarterR" label="우뒤펜" x={198} y={284} w={34}  h={68} rx={4} />

        <rect x="22" y="55"  width="10" height="30" rx="4" fill="#ddd" opacity="0.5" />
        <rect x="228" y="55" width="10" height="30" rx="4" fill="#ddd" opacity="0.5" />
        <rect x="22" y="300" width="10" height="30" rx="4" fill="#ddd" opacity="0.5" />
        <rect x="228" y="300" width="10" height="30" rx="4" fill="#ddd" opacity="0.5" />
      </svg>

      {/* 범례 */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
        {PRESETS.map(function(p) {
          return (
            <span key={p.key} style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: p.bg, border: '1.2px solid '+p.color, display: 'inline-block' }} />
              <span style={{ color: '#666' }}>{p.label} {p.desc}</span>
            </span>
          );
        })}
      </div>

      {selectedPart && (
        <PartInputModal
          partKey={selectedPart}
          currentValue={value[selectedPart]}
          onSelect={handleSelect}
          onClear={handleClear}
          onClose={function(){setSelectedPart(null);}}
        />
      )}
    </div>
  );
}

export default AuctionPaintMap;
